import React, { useEffect, useState } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { getRecentlyViewedIds } from '../utils/recentlyViewed';
import ProductMediaWithSwatches from './ProductMediaWithSwatches';

/**
 * "Recently Viewed" strip - reads product IDs from localStorage (see
 * utils/recentlyViewed.js) and fetches each one individually via GET
 * /api/products/:id. A handful of parallel requests for a nice-to-have feature is an
 * acceptable tradeoff against adding a dedicated bulk-fetch-by-ids endpoint.
 * Renders nothing (not even a heading) when there's no history yet, or on a fresh
 * product page where this IS the product being viewed (via excludeId).
 */
export default function RecentlyViewed({ excludeId, limit = 8, title = 'Recently Viewed' }) {
  const [products, setProducts] = useState([]);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const ids = getRecentlyViewedIds(excludeId).slice(0, limit);
    if (ids.length === 0) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      ids.map((id) =>
        fetch(`/api/products/${id}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      const byId = new Map();
      results.forEach((r) => {
        const p = r && r.success ? r.data || r.product : null;
        if (p) byId.set(p.id, p);
      });
      // Preserve recency order - Promise.all resolves in the order requested, but
      // fetches can still complete/settle in a way that's easy to get backwards here.
      setProducts(ids.map((id) => byId.get(id)).filter(Boolean));
    });
    return () => {
      cancelled = true;
    };
  }, [excludeId, limit]);

  if (products.length === 0) return null;

  return (
    <section className="py-8 sm:py-10">
      <div className="container mx-auto max-w-full px-3 sm:px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{title}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {products.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
            >
              <ProductMediaWithSwatches
                product={product}
                linkClassName="block"
                containerClassName="aspect-square bg-white flex items-center justify-center"
                imgClassName="w-full h-full object-contain p-2 group-hover:scale-[1.02] transition-transform duration-200"
              />
              <div className="px-2.5 pb-2.5">
                <div className="mt-2 text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 min-h-[2.25rem]">
                  {product.name}
                </div>
                <div className="mt-1 text-sm sm:text-base font-bold text-indigo-600">
                  {formatPrice(product.price)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
